import { useEffect, useState } from 'react';

import Icon from '@components/icon/Icon';
import RadioGroup from '@components/radio-button/RadioGroup';
import { PaymentCollectionType } from '@features/group-management/constants';
import { CardProps } from '@types';
import { getOrdinalDateString } from '@utils/date';

import {
  byTermCardOptions,
  ByTermKeys,
  monthCardOptions,
  MonthDay
} from '../constants';
import { isFirstDayFrequencyEquivalent } from '../helper';

import theme from '../../../../themes/colors';

const TermCard = ({
  handleActivationDate,
  handleIntervalFrequency,
  selectedDate,
  selectedFrequency,
  type,
  isTermDisabled,
  isCustomTerm,
  hideAdvanced,
  hideFirstDayFrequency
}: // nextDueDate
CardProps) => {
  const [selectedDay, setSelectedDay] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>();
  const [selectedTermType, setSelectedTermType] = useState<string>(
    isCustomTerm ? ByTermKeys.ADVANCED : ByTermKeys.BASIC
  );
  const [selectedTerm, setSelectedTerm] = useState<number>(2);
  const [isDayPickerVisible, setIsDayPickerVisible] = useState<boolean>(false);

  const isFirstDayAlreadyInUse =
    type === PaymentCollectionType.BY_TERM &&
    isFirstDayFrequencyEquivalent(selectedDate);
  const shouldHideFirstDayFrequency =
    hideFirstDayFrequency && !isFirstDayAlreadyInUse;

  const handleDayClick = (item: string) => {
    setSelectedDay(item);
    // const dueDate = handleTermDateSelect({
    //   nextDueDate,
    //   item
    // });
    // handleActivationDate(new Date(dueDate));
    handleActivationDate(item);
  };

  const handleTypeSelect = (item: string) => {
    setSelectedType(item);
    if (item !== MonthDay.CUSTOM) {
      setIsDayPickerVisible(false);
      setSelectedDay('');
      handleActivationDate('1');
    }
    if (item === MonthDay.LAST) {
      // const isDueMonth =
      //   new Date(nextDueDate).getMonth() === new Date().getMonth();
      // const nextDueMonth = isDueMonth
      //   ? new Date(nextDueDate).getMonth()
      //   : new Date().getMonth();
      // const lastDay = getLastDayOfMonthUTC(nextDueMonth);
      // const lastDayDate = lastDay.getUTCDate();
      // const dueDate = handleTermDateSelect({
      //   nextDueDate,
      //   item: lastDayDate.toString()
      // });
      // handleActivationDate(new Date(dueDate));
      handleActivationDate('last_day');
    }
    if (item === MonthDay.FIRST) {
      // const dueDate = handleTermDateSelect({
      //   nextDueDate,
      //   item: '1'
      // });
      // handleActivationDate(new Date(dueDate));
      handleActivationDate('first_day');
    }
    if (item === MonthDay.CUSTOM && selectedDay === '') {
      setSelectedDay('1');
      setIsDayPickerVisible(true);
      handleActivationDate('1');
    }
  };

  const handleBasicTermSelection = () => {
    if (!selectedDate || type !== PaymentCollectionType.BY_TERM) {
      // const dueDate = handleTermDateSelect({ nextDueDate, item: '1' });
      // handleActivationDate(new Date(dueDate));
      if (shouldHideFirstDayFrequency) {
        setSelectedType(MonthDay.LAST);
        handleActivationDate('last_day');
      } else {
        setSelectedType(MonthDay.FIRST);
        handleActivationDate('first_day');
      }
    } else if (selectedDate && type === PaymentCollectionType.BY_TERM) {
      // const dayType = getDayType(selectedDate);
      // setSelectedType(dayType);
      if (selectedDate === 'first_day') {
        setSelectedType(MonthDay.FIRST);
        handleActivationDate('first_day');
      } else if (selectedDate === 'last_day') {
        setSelectedType(MonthDay.LAST);
        handleActivationDate('last_day');
      } else {
        setSelectedType(MonthDay.CUSTOM);
        setIsDayPickerVisible(true);
        handleActivationDate(selectedDate as string);
        setSelectedDay(selectedDate as string);
      }
      // if (dayType === MonthDay.CUSTOM) {
      //   const day = new Date(selectedDate).getDate();
      //   setSelectedDay(String(day));
      // }
    }
  };

  const handleTermTypeSelect = (item: string) => {
    setSelectedTermType(item);
    if (item === ByTermKeys.ADVANCED) handleActivationDate(item);
    else handleBasicTermSelection();
  };

  const handleAdd = () => {
    if (selectedTerm <= 11) setSelectedTerm((prev) => prev + 1);
  };

  const handleMinus = () => {
    if (selectedTerm >= 3) setSelectedTerm((prev) => prev - 1);
  };

  const handleBack = () => {
    if (selectedDay === '') setSelectedType(MonthDay.FIRST);
    setIsDayPickerVisible(false);
  };

  useEffect(() => {
    handleIntervalFrequency(String(selectedTerm));
    if (selectedDate) {
      handleActivationDate(selectedDate as string);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTerm]);

  useEffect(() => {
    handleBasicTermSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    if (!selectedFrequency || selectedFrequency <= 1) setSelectedTerm(2);
    else setSelectedTerm(Number(selectedFrequency));
  }, [selectedFrequency]);

  useEffect(() => {
    if (isCustomTerm && selectedDay === '')
      handleActivationDate(ByTermKeys.ADVANCED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay]);

  const visibleDays = Array.from({ length: 28 }, (_, i) => i + 1).filter(
    (day) => !(shouldHideFirstDayFrequency && day === 1)
  );

  const visibleMonthCardOptions = shouldHideFirstDayFrequency
    ? monthCardOptions.filter((option) => option.key !== MonthDay.FIRST)
    : monthCardOptions;

  const renderCustomDayPicker = () => (
    <>
      <div className="mb-4 mt-2 flex w-full gap-1.5 text-sm font-normal">
        <Icon name="back" className="cursor-pointer" onClick={handleBack} />
        <div>Select custom day</div>
      </div>
      <div className="grid grid-cols-7 gap-2 pb-6 pt-1">
        {visibleDays.map((day) => (
          <div
            key={day}
            className={`flex size-[46px] cursor-pointer items-center justify-center rounded-lg border border-cofeeGreyBlue5 text-content ${
              Number(selectedDay) === day
                ? 'border-cofeePrimary6 bg-cofeeGreen1'
                : ''
            }`}
            role="presentation"
            onClick={() => {
              handleDayClick(String(day));
            }}
          >
            {day}
          </div>
        ))}
      </div>
    </>
  );

  const renderFirstLastOrCustomOptions = () => (
    <>
      <RadioGroup
        options={visibleMonthCardOptions}
        handleChange={handleTypeSelect}
        orientation="vertical"
        selectedValue={selectedType}
        className="!w-full"
      />
      {!!selectedDay && (
        <div className="-mt-4 mb-4 flex w-full items-center gap-2 pl-8 text-sm text-grey">
          <div>{`(${getOrdinalDateString(selectedDay)} of the month)`}</div>
          <Icon
            className="size-3 cursor-pointer"
            name="edit"
            stroke={theme.primary}
            size="none"
            onClick={() => setIsDayPickerVisible(true)}
          />
        </div>
      )}
    </>
  );

  return (
    <>
      {!hideAdvanced && (
        <RadioGroup
          options={byTermCardOptions}
          handleChange={handleTermTypeSelect}
          orientation="horizontal"
          selectedValue={selectedTermType}
          disabled={isTermDisabled}
        />
      )}
      {hideAdvanced || selectedTermType === ByTermKeys.BASIC ? (
        <div className="mb-3 mt-4 flex flex-col items-center justify-center">
          {!isTermDisabled && (
            <div className="mb-4 flex items-center justify-center gap-4">
              <div className="rounded-lg border border-grey40 p-3">
                <Icon
                  name="minus"
                  stroke={selectedTerm > 2 ? theme.primary : theme.grey}
                  onClick={handleMinus}
                  size="none"
                  className="size-6 cursor-pointer"
                />
              </div>
              <div className="flex h-13.5 w-36 items-center justify-center rounded-xl border border-grey40 bg-white text-2xl">
                {selectedTerm}
              </div>
              <div className="rounded-lg border border-grey40 p-3">
                <Icon
                  name="plus"
                  stroke={selectedTerm < 12 ? theme.primary : theme.grey}
                  onClick={handleAdd}
                  size="none"
                  className="size-6 cursor-pointer"
                />
              </div>
            </div>
          )}
          {!isTermDisabled && (
            <div className="mb-6 flex w-full items-center justify-center gap-2">
              {[3, 6, 12].map((item) => (
                <div
                  key={item}
                  className={`${
                    selectedTerm === item
                      ? 'border-cofeePrimary6 text-cofeePrimary6'
                      : 'border-grey40 text-grey'
                  } w-1/3 cursor-pointer rounded-lg border bg-white px-2 py-1.5 text-center text-sm`}
                  role="presentation"
                  onClick={() => setSelectedTerm(item)}
                >
                  {`${String(item)} months`}
                </div>
              ))}
            </div>
          )}
          {isDayPickerVisible
            ? renderCustomDayPicker()
            : renderFirstLastOrCustomOptions()}
        </div>
      ) : (
        <div className="my-4 flex items-start gap-2 rounded-lg border-l-6 border-cofeeBlue9 bg-cofeeBlue1 p-2 text-sm text-grey">
          <div className="flex size-4.5 min-h-4.5 min-w-4.5 shrink-0 items-start justify-center pt-0.5">
            <Icon
              name="alert-circle"
              stroke={theme.cofeeBlue9}
              size="large"
              className="shrink-0"
            />
          </div>
          <span className="min-w-0 leading-normal text-cofeeGreyBlue8">
            Fixed total split into sequential installments with configurable
            dates, discount options, and payer pay-ahead. Also supports existing
            advanced term flexibility.
          </span>
        </div>
      )}
    </>
  );
};

export default TermCard;
